import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../Toast/Toast';
import notificationService from '../../services/notificationService';

/**
 * Notification Manager Component
 * Polls for new collaboration invitations and shows toast notifications
 * Should be mounted once at app level
 */
const NotificationManager = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const isPolling = useRef(false);

  useEffect(() => {
    // Only start polling if user is authenticated
    if (!localStorage.getItem('currentUser') || isPolling.current) return;

    isPolling.current = true;

    // Callback for new invitations
    const handleNewInvitation = (invitation) => {
      console.log('🔔 New collaboration invitation:', invitation);

      const fileName = invitation.file?.name || 'a file';
      const ownerName = invitation.owner
        ? `${invitation.owner.firstName || ''} ${invitation.owner.lastName || ''}`.trim() || invitation.owner.email
        : 'Someone';

      // Dispatch custom event for other components to listen to
      document.dispatchEvent(new CustomEvent('newCollaborationInvitation', {
        detail: { invitation }
      }));

      // Show toast with action button
      if (toast && toast.showToast) {
        toast.showToast({
          message: `${ownerName} invited you to collaborate on "${fileName}"`,
          type: 'invitation',
          duration: 10000, // 10 seconds
          action: {
            label: 'View',
            onClick: () => {
              // Trigger event to open collaboration sidebar to invites tab
              document.dispatchEvent(new CustomEvent('openCollaborationTab', {
                detail: { tab: 'invites' }
              }));
            }
          }
        });
      }

      // Play notification sound (optional)
      try {
        const audio = new Audio('/notification.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {
          // Silently fail if audio doesn't play (autoplay restrictions)
        });
      } catch (e) {
        // Ignore audio errors
      }
    };

    // Start polling every 30 seconds
    notificationService.startPolling(handleNewInvitation, 30000);

    // Cleanup on unmount
    return () => {
      notificationService.stopPolling();
      isPolling.current = false;
    };
  }, [toast, navigate]);

  // This component doesn't render anything
  return null;
};

export default NotificationManager;
